import {
  Badge,
  Button,
  Flex,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Portal,
  Text,
  Tooltip,
  useColorMode,
  useToast,
} from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { Locale } from '@texas/i18n/types';
import { formatDate } from '@texas/utils/helpers/dateHelper';
import { TFunction } from 'i18next';
import { defaultIconSize, Icons } from '../shared/Icons';
import { reactSelectStyle, reactSelectTheme } from '@texas/resources/styles';
import CreatableSelect from 'react-select/creatable';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import {
  ReceiverUser,
  shipmentsApi,
  ShipmentState,
} from '@texas/api/endpoints/shipmentsApi';
import { useValueDisclosure } from '@texas/hooks/useValueDisclosure';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useTranslation } from 'react-i18next';
import {
  compareEnum,
  ensureEnumNumber,
} from '@texas/utils/helpers/enumHelpers';
import { SampleReceiver } from '@texas/api/endpoints/samplesApi';
import { transportRequestEvents } from '../shared/transportRequests/events';
import {
  TransportRequest,
  TransportRequestShipment,
} from '@texas/api/endpoints/transportRequestApi';
import { AddBrandShipmentModal } from '../shared/shipment/add/AddBrandShipmentModal';
import { AddInternalShipmentModal } from '../shared/shipment/add/AddInternalShipmentModal';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { EditShipmentModal } from '../shared/shipment/EditShipmentModal';
import { AngularNavLink } from '../shared/AngularNavLink';
import { InputComponent } from './TrackingValueComponent';
import { InputActionMeta } from 'react-select';
import React from 'react';
import dayjs from 'dayjs';

interface Props {
  t: TFunction;
  onChange: () => void;
  onSamplesClick: Dispatch<SetStateAction<number>>;
  onEditTrClick: Dispatch<SetStateAction<number>>;
}

export const transportRequestColumns = ({
  t,
  onChange,
  onSamplesClick,
  onEditTrClick,
}: Props) => {
  const columnHelper = createColumnHelper<TransportRequest>();
  const columns = [
    columnHelper.accessor('shipment', {
      cell: (info) => {
        const shipment = info.getValue();
        const isPending = !shipment;
        const isDispatched = Boolean(shipment?.dispatchDate);

        return (
          <Tooltip
            label={
              isPending
                ? t('shipment.awaitingShipment')
                : isDispatched
                ? t('shipment.dispatched')
                : t('shipment.assigned')
            }
          >
            <Icons.Circle
              boxSize={3}
              color={
                isPending
                  ? 'yellow.400'
                  : isDispatched
                  ? 'green.500'
                  : 'entityDirectory.primaryBackground'
              }
              borderRadius="full"
            />
          </Tooltip>
        );
      },
      size: 42,
      enableSorting: false,
      header: t('general.state'),
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('id', {
      cell: (info) => {
        if (info.row.original.shipment !== null)
          return <Text fontWeight="semibold">#{info.getValue()}</Text>;
        return (
          <Tooltip label={t('transportRequest.update')}>
            <Text
              onClick={() => onEditTrClick(info.row.original.id)}
              cursor="pointer"
              fontWeight="semibold"
              textDecoration="underline"
              textDecorationColor="entityDirectory.border"
              textUnderlineOffset="3px"
            >
              #{info.getValue()}
            </Text>
          </Tooltip>
        );
      },
      size: 76,
      header: t('transportRequest.ticket'),
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('samplesInTransport', {
      cell: (info) => (
        <Tooltip label={t('sample.included')}>
          <Button
            minW="36px"
            variant="entityDirectorySecondary"
            size="xs"
            onClick={() => onSamplesClick(info.row.original.id)}
          >
            {info.getValue()}
          </Button>
        </Tooltip>
      ),
      size: 76,
      enableSorting: false,
      header: t('sample.samples'),
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('createdByUserName', {
      cell: (info) => (
        <Flex align="center" gap={3} minW={0}>
          <RouteParty
            label={info.row.original.branchIdentifier}
            name={info.getValue()}
          />
          <Icons.ArrowRightThin
            boxSize={5}
            color="entityDirectory.mutedText"
            flexShrink={0}
          />
          <RouteParty
            label={info.row.original.receiverUser.label}
            name={info.row.original.receiverUser.name}
          />
        </Flex>
      ),
      size: 340,
      header: t('shipment.route'),
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('note', {
      cell: (info) => info.getValue(),
      enableSorting: false,
      header: t('general.notes'),
      meta: { toggleable: true, defaultVisible: false },
    }),
    columnHelper.accessor('requestedDispatchDate', {
      cell: (info) => (
        <Tooltip
          label={
            info.row.original.asapShipment
              ? t('transportRequest.asapNote')
              : null
          }
        >
          <Flex direction="column" gap={0.5}>
            <Flex gap={2} align="center">
              {formatDate(Locale.En, info.getValue())}
              {info.row.original.asapShipment && (
                <Icons.AlertCircle boxSize={4} color="yellow.300" />
              )}
            </Flex>
            <RelativeRequestedDate date={info.getValue()} />
          </Flex>
        </Tooltip>
      ),

      header: t('transportRequest.reqDispatchDate'),
      size: 145,
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('shipment.dispatchDate', {
      cell: (info) => {
        const date = info.row.original.shipment?.dispatchDate;

        if (!date) return null;
        return formatDate(Locale.En, date);
      },
      header: t('shipment.dispatchDate'),
      size: 120,
      meta: { toggleable: true, defaultVisible: false },
    }),
    columnHelper.accessor('shipment.trackingNumber', {
      cell: (info) => {
        return (
          <React.Fragment key={info.row.original.id}>
            <ChangeTrackingNumber
              onChange={onChange}
              samplesInTransport={info.row.original.samplesInTransport}
              receiverUser={info.row.original.receiverUser}
              branchId={info.row.original.branchId}
              trId={info.row.original.id}
              shipment={info.row.original.shipment}
              requestedDispatchDate={formatDate(
                Locale.En,
                info.row.original.requestedDispatchDate,
              )}
              receiver={ensureEnumNumber(
                SampleReceiver,
                info.row.original.sampleReceiver,
              )}
            />
          </React.Fragment>
        );
      },
      size: 260,
      enableSorting: false,
      header: t('shipment.trackingNumber'),
      meta: { toggleable: true, defaultVisible: true },
    }),
    columnHelper.accessor('shipment.courier', {
      cell: (info) => {
        const courier = info.row.original.shipment?.courier;

        if (!courier) return null;
        return courier;
      },
      header: t('shipment.courier'),
      size: 100,
      meta: { toggleable: true, defaultVisible: true },
    }),

    columnHelper.accessor('shipment.receiverName', {
      cell: (info) => {
        const receiverName = info.row.original.shipment?.receiverName;
        const externalShipment = compareEnum(
          SampleReceiver,
          info.row.original.sampleReceiver,
          SampleReceiver.Brand,
        );

        if (externalShipment) {
          const customer = info.row.original.shipment?.receiverCustomer;
          return (
            <Flex direction="column" gap={1}>
              {customer ? (
                <Badge w="fit-content" variant="entity-directory-reference">
                  <AngularNavLink
                    variant="entityDirectoryBadge"
                    whiteSpace="break-spaces"
                    to="customer"
                    navobj={{
                      customerId:
                        info.row.original.shipment?.receiverCustomerId,
                    }}
                  >
                    {customer}
                  </AngularNavLink>
                </Badge>
              ) : null}
            </Flex>
          );
        }

        const branch = info.row.original.shipment?.receiverBranch;

        return (
          <Flex direction="column" gap={1}>
            {branch ? (
              <Tooltip label={receiverName}>
                <Badge w="fit-content" variant="entity-directory-reference">
                  {branch}
                </Badge>
              </Tooltip>
            ) : (
              <Text variant="sub">{receiverName}</Text>
            )}
          </Flex>
        );
      },
      enableSorting: false,
      header: t('shipment.receiver'),
      size: 160,
      meta: { toggleable: true, defaultVisible: true },
    }),
  ];

  return columns;
};

function RouteParty({ label, name }: { label: string; name: string }) {
  return (
    <Flex direction="column" flex="1 1 0" minW={0}>
      <Text
        fontWeight="medium"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {name}
      </Text>
      <Text
        color="entityDirectory.mutedText"
        fontSize="11px"
        overflow="hidden"
        textOverflow="ellipsis"
        whiteSpace="nowrap"
      >
        {label}
      </Text>
    </Flex>
  );
}

function RelativeRequestedDate({ date }: { date: string }) {
  const { t } = useTranslation();
  const days = dayjs(date).startOf('day').diff(dayjs().startOf('day'), 'day');
  const label =
    days === 0
      ? t('shipment.relativeToday')
      : days === -1
      ? t('shipment.relativeYesterday')
      : days === 1
      ? t('shipment.relativeTomorrow')
      : days < 0
      ? t('shipment.relativeDaysAgo', { count: Math.abs(days) })
      : t('shipment.relativeInDays', { count: days });

  return (
    <Text
      color={days < 0 ? 'red.600' : 'entityDirectory.mutedText'}
      fontSize="11px"
      fontWeight={days < 0 ? 'semibold' : 'normal'}
    >
      {label}
    </Text>
  );
}

function ChangeTrackingNumber({
  trId,
  shipment,
  receiverUser,
  branchId,
  receiver,
  requestedDispatchDate,
  samplesInTransport,
  onChange,
}: {
  trId: number;
  receiverUser: ReceiverUser;
  branchId: number;
  shipment: TransportRequestShipment | null;
  receiver: SampleReceiver;
  requestedDispatchDate: string;
  samplesInTransport: number;
  onChange: () => void;
}) {
  const { colorMode } = useColorMode();
  const { data, refetch, loading } = useApiResource(
    shipmentsApi.availableFromTransportRequest,
  );
  const { value, isOpen, onClose, onOpen } = useValueDisclosure<string>();
  const {
    value: shipmentChangeValue,
    isOpen: isShipmentChangeOpen,
    onClose: onShipmentChangeClose,
    onOpen: onShipmentChangeOpen,
  } = useValueDisclosure<number>();
  const { request: addRequest, loading: addLoading } = useApiRequest(
    shipmentsApi.addTransportToShipment,
  );
  const { request: removeRequest, loading: removeLoading } = useApiRequest(
    shipmentsApi.removeTransportFromShipment,
  );
  const toast = useToast();
  const { t } = useTranslation();

  const [ctrlShipment, setCtrlShipment] = useState<
    { tracking: string; id: number } | undefined
  >(
    shipment
      ? { tracking: shipment.trackingNumber, id: shipment.id }
      : undefined,
  );

  const [inputValue, setInputValue] = useState(
    shipment ? shipment.trackingNumber : '',
  );

  useEffect(() => {
    setCtrlShipment(
      shipment
        ? { tracking: shipment.trackingNumber, id: shipment.id }
        : undefined,
    );
  }, [trId, shipment]);

  const performRemove = async () => {
    await request(
      removeRequest,
      [trId],
      () => {
        transportRequestEvents.removedFromShipment.dispatch();
        setCtrlShipment(undefined);
        onChange();
      },
      (error) => {
        toast({
          title: t('general.updateFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
  };

  const onInputChange = (inputValue: string, { action }: InputActionMeta) => {
    if (action === 'input-change') {
      setInputValue(inputValue);
    }
  };

  const performChange = async (
    shipmentId: number | undefined,
    tracking: string | undefined,
  ) => {
    setInputValue(tracking ? tracking : '');
    if (shipmentId && tracking) {
      await request(
        addRequest,
        [{ transportRequestId: trId, shipmentId: shipmentId }],
        () => {
          setCtrlShipment({
            id: shipmentId,
            tracking,
          });
          transportRequestEvents.addedToShipment.dispatch();
          onChange();
        },
        (error) => {
          toast({
            title: t('general.updateFailed'),
            description: error.message,
            status: 'error',
            isClosable: true,
          });
        },
      );
      return;
    }

    await request(
      removeRequest,
      [trId],
      () => {
        transportRequestEvents.removedFromShipment.dispatch();
        setCtrlShipment(undefined);
        onChange();
      },
      (error) => {
        toast({
          title: t('general.updateFailed'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
  };

  return (
    <Flex gap={2} alignItems="center" justify="space-between">
      {ensureEnumNumber(ShipmentState, shipment?.state) ===
        ShipmentState.AwaitingDispatch || shipment?.state === undefined ? (
        <CreatableSelect
          styles={{
            ...reactSelectStyle<false>({
              mode: colorMode,
              variant: 'entity-directory',
            }),
            menuPortal: (base) => ({ ...base, zIndex: 1401 }),
          }}
          value={
            ctrlShipment
              ? { label: ctrlShipment.tracking, value: ctrlShipment.id }
              : null
          }
          isLoading={loading || addLoading || removeLoading}
          menuPortalTarget={document.body}
          noOptionsMessage={() => <>No shipments</>}
          theme={(theme) => reactSelectTheme(theme, colorMode)}
          onMenuOpen={() => refetch(trId)}
          options={data?.map((d) => ({
            label: d.trackingNumber,
            value: d.id,
          }))}
          onCreateOption={(v) => onOpen(v)}
          onChange={async (e) => await performChange(e?.value, e?.label)}
          placeholder={t('shipment.selectOrCreateNew')}
          controlShouldRenderValue={false}
          onInputChange={onInputChange}
          components={{ Input: InputComponent }}
          inputValue={inputValue}
        />
      ) : (
        <Text>{ctrlShipment?.tracking}</Text>
      )}
      {ctrlShipment && (
        <Menu isLazy={true}>
          <MenuButton
            as={IconButton}
            size="sm"
            variant="ghost"
            aria-label={t('shipment.menu')}
            icon={<Icons.DotsHorizontal boxSize={defaultIconSize} />}
          />
          <Portal>
            <MenuList>
              <MenuItem
                onClick={() => onShipmentChangeOpen(ctrlShipment.id)}
                icon={<Icons.Pencil boxSize={defaultIconSize} />}
              >
                {t('shipment.editShipment')}
              </MenuItem>
              {!compareEnum(
                ShipmentState,
                shipment?.state,
                ShipmentState.Dispatched,
              ) && (
                <MenuItem
                  onClick={performRemove}
                  icon={<Icons.Cancel boxSize={defaultIconSize} />}
                >
                  {t('transportRequest.removeFromShipment')}
                </MenuItem>
              )}
            </MenuList>
          </Portal>
        </Menu>
      )}
      {value && (
        <>
          {receiver !== SampleReceiver.Brand && (
            <AddInternalShipmentModal
              toUserId={receiverUser.id}
              ticketNumber={trId}
              samplesCount={samplesInTransport}
              onCreate={(data) => {
                refetch(trId);
                performChange(data.id, data.trackingNumber);
              }}
              defaultTrackingNumber={value}
              requestedDispatchDate={requestedDispatchDate}
              isOpen={isOpen}
              onClose={onClose}
            />
          )}
          {receiver === SampleReceiver.Brand && (
            <AddBrandShipmentModal
              contact={receiverUser}
              ticketNumber={trId}
              samplesCount={samplesInTransport}
              branchId={branchId}
              onCreate={(data) => {
                refetch(trId);
                performChange(data.id, data.trackingNumber);
              }}
              defaultTrackingNumber={value}
              requestedDispatchDate={requestedDispatchDate}
              isOpen={isOpen}
              onClose={onClose}
            />
          )}
        </>
      )}
      {shipmentChangeValue && (
        <EditShipmentModal
          onChange={onChange}
          shipmentId={shipmentChangeValue}
          isOpen={isShipmentChangeOpen}
          onClose={onShipmentChangeClose}
        />
      )}
    </Flex>
  );
}
